import {
  createContext,
  createElement,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import {MenuItem} from '../types/MenuItem'
import {CompanyFeature} from '../types/CompanyFeature'
import {getCompanyFeatures, getDashboardMenu} from '../api/index'
import {isSuperAdmin} from '../modules/auth'
import {getToolsMenu, getLojistasMenu, normalizeMenu} from '../data-layer/MenuData'
import {isLojistasOnlyAccess} from '../data-layer/menuAccess'

type UserMenuContextValue = {
  menuData: MenuItem[]
  isFetching: boolean
  isLojistasOnly: boolean
}

const UserMenuContext = createContext<UserMenuContextValue | undefined>(undefined)

export const UserMenuProvider = ({children}: PropsWithChildren<{}>) => {
  const [menuData, setMenuData] = useState<MenuItem[]>([])
  const [isFetching, setIsFetching] = useState<boolean>(true)
  const [isLojistasOnly, setIsLojistasOnly] = useState(false)

  const fetchDashboardMenu = useCallback(async () => {
    const response = await getDashboardMenu()
    const menuItems = (response.data || []).filter(
      (item: MenuItem) => item.children && item.children.length > 0
    )
    return menuItems
  }, [])

  const fetchData = useCallback(async () => {
    setIsFetching(true)

    const result = await getCompanyFeatures()
    const features: CompanyFeature[] = result.data || []

    const dashboardMenu = await fetchDashboardMenu()
    const superAdmin = isSuperAdmin()
    const toolsMenu = getToolsMenu(features, superAdmin)
    const lojistasMenu = getLojistasMenu(features)

    const items = normalizeMenu([...dashboardMenu, ...toolsMenu, ...lojistasMenu])

    setMenuData(items)
    setIsLojistasOnly(
      isLojistasOnlyAccess({
        features,
        dashboardMenu,
        toolsMenu,
        lojistasMenu,
        superAdmin,
      })
    )
    setIsFetching(false)
  }, [fetchDashboardMenu])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const value = useMemo(
    () => ({menuData, isFetching, isLojistasOnly}),
    [menuData, isFetching, isLojistasOnly]
  )

  return createElement(UserMenuContext.Provider, {value}, children)
}

export const useUsermenuData = (): UserMenuContextValue => {
  const context = useContext(UserMenuContext)
  if (!context) {
    throw new Error('useUsermenuData deve ser usado dentro de UserMenuProvider')
  }
  return context
}
